import React, { useEffect, useRef, memo, useCallback } from 'react'
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import './customQuill.css';
import undoSvg from '../../../images/undo.svg'; 
import redoSvg from '../../../images/redo.svg'; 

interface props{
    title:string;
    value?: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
}

const QuillInput = memo(function QuillInput({title, value, onChange, disabled = false}:props) {
    const quillRef = useRef<HTMLDivElement>(null);
    const quillInstanceRef = useRef<Quill | null>(null);
    const isInitializedRef = useRef(false);
    const lastValueRef = useRef<string | undefined>(value);

    // Initialize Quill instance
    useEffect(() => {
        if (quillRef.current && !isInitializedRef.current) {
            // Cast icons to 'any' to avoid type issues
            const icons = Quill.import('ui/icons') as any;
            icons['undo'] = `<img src="${undoSvg}" class="ql-custom-icon" alt="Undo"/>`;
            icons['redo'] = `<img src="${redoSvg}" class="ql-custom-icon" alt="Redo"/>`;

            quillInstanceRef.current = new Quill(quillRef.current, {
                theme: 'snow',
                modules: {
                    history: {
                        delay: 2000,
                        maxStack: 500,
                        userOnly: true
                    },
                    toolbar: disabled ? false : {
                        container: [
                            ['undo', 'redo'],
                            [{ header: [true,1, 2, 3,4,5,6] }], 
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ color: [] }, { background: [] }],
                            [{ list: 'ordered' }, { list: 'bullet' }],
                            [{ indent: '-1' }, { indent: '+1' }],
                            [{ align: [] }],
                            ['blockquote', 'code-block'],
                            ['link', 'image', 'video']
                        ],
                        handlers: {
                            undo: function () {
                                (this as any).quill.history.undo();
                            },
                            redo: function () {
                                (this as any).quill.history.redo();
                            }
                        }
                    }
                }
            });

            // Set initial value
            if (value !== undefined) {
                quillInstanceRef.current.root.innerHTML = value;
                lastValueRef.current = value;
            }

            // Set up text change handler with debounce
            let timeoutId: NodeJS.Timeout;
            quillInstanceRef.current.on('text-change', () => {
                if (onChange) {
                    clearTimeout(timeoutId);
                    timeoutId = setTimeout(() => {
                        const newValue = quillInstanceRef.current?.root.innerHTML || '';
                        if (newValue !== lastValueRef.current) {
                            lastValueRef.current = newValue;
                            onChange(newValue);
                        }
                    }, 300);
                }
            });

            isInitializedRef.current = true;
        }

        return () => {
            if (quillInstanceRef.current) {
                quillInstanceRef.current = null;
                isInitializedRef.current = false;
            }
        };
    }, [disabled]); // Add disabled to dependencies

    // Handle value updates
    useEffect(() => {
        if (quillInstanceRef.current && value !== undefined && value !== lastValueRef.current) {
            quillInstanceRef.current.root.innerHTML = value;
            lastValueRef.current = value;
        }
    }, [value]);

    return (
        <div className='bg-white rounded-[16px] pb-[32px] pt-[16px] flex flex-col gap-[16px]'>
            <p className='text-[20px] font-semibold px-[24px]'>{title}</p>
            <div 
                ref={quillRef} 
                style={{ height: '300px' }} 
                className={disabled ? 'pointer-events-none opacity-70' : ''}
            />
        </div>
    );
});

export default QuillInput;